import { useState, useCallback } from 'react';

/**
 * useSnackbars
 *
 * Owns the prompt-snackbar state plus the three snackbar methods that were
 * previously class methods on FormViewer:
 *
 *   showErrorSnackbar(msg)  — dispatch a Redux error snackbar (no state)
 *   showPromptSnackbar(msg) — show the bottom prompt snackbar
 *   hidePromptSnackbar()    — hide the prompt snackbar
 *                             (guarded: no-op if stmcode or choices is active)
 *
 * State returned for render:
 *   promptSnackbar        — boolean open/closed flag
 *   promptSnackbarMessage — message string
 *
 * Note: the legacy errorSnackbar / errorMsg / errVertical / errHorizontal
 * class-state fields were never mutated (always stayed at their initial
 * values) so they are not reproduced here.
 */
const useSnackbars = ({
  showSnackbarMessage, // Redux prop: (msg, type, duration, anchorOrigin) => void
  fieldEventsRef, // { current: { stmcodeRef, choicesRef } } — used by hidePromptSnackbar
}) => {
  const [promptSnackbar, setPromptSnackbar] = useState(false);
  const [promptSnackbarMessage, setPromptSnackbarMessage] = useState('');

  const showErrorSnackbar = useCallback(
    msg => {
      showSnackbarMessage(msg, 'error', 3500, { vertical: 'top', horizontal: 'center' });
    },
    [showSnackbarMessage],
  );

  const showPromptSnackbar = useCallback(msg => {
    setPromptSnackbar(true);
    setPromptSnackbarMessage(msg);
  }, []);

  const hidePromptSnackbar = useCallback(() => {
    if (fieldEventsRef.current?.stmcodeRef?.current) return;
    if (fieldEventsRef.current?.choicesRef?.current) return;
    setPromptSnackbar(false);
    setPromptSnackbarMessage('');
  }, [fieldEventsRef]);

  return {
    promptSnackbar,
    promptSnackbarMessage,
    showErrorSnackbar,
    showPromptSnackbar,
    hidePromptSnackbar,
  };
};

export default useSnackbars;
